import { SHEET_KEYS } from '../../../models/sheet-keys';
import {
  PureSheetValidationResult,
  SheetValidationContext,
  SheetValidator,
} from '../sheet-validator';
import { differsAtCentPrecision } from '../validation-field-key.util';
import {
  areRole19YearsConsecutive,
  buildRole19EquityColumnKey,
  buildRole19SfpKey,
  extractRole19SchemaTaxonomy,
  getRole19EquityCrossSheetTargets,
  getRole19TotalColumnQname,
  isRole19CalculatedField,
  isRole19Empty,
  isRole19SectionHeader,
  isRole19StaticallyRequired,
  pickRole19Role8Schema,
  pickRole19Schema,
  resolveRole19ColumnVisibility,
  role19AccountingStandardToTaxonomy,
  role19ReferencedSheetKeys,
  role19SchemaForReferencedSheet,
  ROLE19_EQUITY_TOTAL_COMPONENT_COLUMNS,
  ROLE19_EQUITY_TOTAL_EXCLUDED_ROW,
  Role19ColumnVisibility,
  Role19Schema,
  Role19SchemaControl,
} from './role19-statement-of-changes-in-equity.rules';
import {
  accountingStandardToXbrlPrefix,
  buildEqualitySnapshot,
  calculator,
  hasCalculatedFieldMismatch,
  isResolvedAccountingStandard,
} from './calc-diff.helper';
import { recomputeReferencedSheets } from '../cross-sheet-recompute.util';

/**
 * Pure validator for Role 19 (Statement of Changes in Equity).
 *
 * Mirrors the editor's role19-610000-statement-of-changes-in-equity.component.ts
 * `evaluateSheetStatus()` surface — the sheet-level `hasErrors` bit that the
 * editor publishes via
 * `stateService.updateSheetLevelStatus('statementOfChangesInEquity', ...)`.
 *
 * The editor's `evaluateSheetStatus()` walks every visible table permutation
 * (Consolidated/Separate × Current/Previous) and any row whose
 * `_computeRowValidationStatus(control, consolidationType, periodType)`
 * returns `'Failed'` flips `hasErrors: true`. Four Failed-category drivers
 * populate the `validationErrors` Map that that method reads from:
 *
 *  1. **Required-field empties** (`validateRequiredFields()`): for every
 *     control where `isRequiredField(control)` is true, every (equity column
 *     × consolidation × period) cell must be non-null. Errors are added to
 *     `validationErrors` unconditionally (no dirty/touched gate on the Map
 *     populator; the dirty-gate only affects Branch-2 of the row-status
 *     computation, which is a SUPERSET of Branch-3 since Branch-3 still
 *     reads the same Map). Row status becomes Failed via Branch-3 because
 *     `This field is mandatory.` is not an API-mismatch message.
 *
 *  2. **Schema-declared cross-sheet equality** (`runEqualityValidations()` →
 *     `EqualityValidationService.validateAllEqualityRules`). SOCIE's MFRS and
 *     MPERS schemas declare three equality blocks against:
 *       - `ProfitLoss` (role 12, SOPL)
 *       - `OtherComprehensiveIncome` (role 15, SOCI)
 *       - `ComprehensiveIncome` (role 15, SOCI)
 *     All of them are column-aware because `statementOfChangesInEquity` is
 *     registered as `equityColumns` in `SHEET_COLUMN_TYPE_MAP`. The
 *     cross-sheet side uses each source sheet's column format (`dualColumn`
 *     for roles 12 / 15 — suffix `[Cons][Period]` on the bare qname).
 *
 *  3. **SOCIE roll-forward — `consecutiveYearEquity`** (per-component): for
 *     every equity column EXCEPT the Total (`EquityMember`), the
 *     `Equity:End` cell of a period must equal the `Equity:Beginning` cell of
 *     the next period. Fires on every Subsequent filing where the Previous-Year
 *     column is visible — NOT gated on the FY boundary being consecutive (see
 *     the CONVERGE-UP note on `checkConsecutiveYearEquityRollForward`). Errors
 *     appear on BOTH the Beginning row (current-year table) and the End row
 *     (previous-year table), column by column.
 *
 *  4. **SOCIE roll-forward — `consecutiveYearEquityTotal`** (total-column
 *     only): same check restricted to the Total column
 *     (`ifrs-full:EquityMember` / `ifrs-smes:EquityMember`); same
 *     Previous-Year-visible (not consecutive-gated) firing condition as #3.
 *
 *  5. **Cross-sheet equity-by-year against Role 8**
 *     (`validateCrossSheetEquityByYear`): SOCIE `Equity:End:EquityMember` per
 *     column must equal SFP `Equity` for the same (dimension × period). Rule
 *     lives on the SOCIE schema's `Equity:End` control. Fires only when
 *     years are consecutive.
 *
 *  6. **Column-sum validation** (`validateColumnSum()`): schema-driven
 *     sub-total / component-column reconciliation. The MFRS schema declares
 *     four `columnSumValidations`, MPERS declares three. Failed means the
 *     sub-total column's value doesn't match the sum of its component
 *     columns for that row × (consolidation × period). Tolerance 0.01 RM.
 *
 *  7. **Equity-total horizontal sum** (`validateEquityTotal()`, MFRS
 *     Consolidated tables only): `EquityMember` cell must equal
 *     `EquityAttributableToOwnersOfParentMember + OtherComponentsOfEquityMember +
 *     NoncontrollingInterestsMember` per row × period. Excludes the
 *     `IncreaseDecreaseThroughAcquisitionOfSubsidiary` row.
 *
 * Drivers intentionally NOT mirrored (would not change `hasErrors`):
 *
 *  - API-mismatch warnings (`validateAgainstApiData`) — these are Suggested
 *    Change (`Validated*`), not Failed, and are filtered out of Branch-3 by
 *    the "does not match the extracted sum" substring check.
 *  - Form-control invalid + dirty/touched — `buildForm()` attaches an EMPTY
 *    `ValidatorFn[] = []` array to every FormControl, so `form.invalid` is
 *    structurally always false. The orchestrator also can't see
 *    dirty/touched on persisted data.
 *  - `updateEquityBeginningEndCellErrors()` — not replayed verbatim, but its
 *    VERDICT contribution is now mirrored by drivers #3 + #4 (which, as of the
 *    2026-06-02 converge-up, fire on the same `hasPreviousYear` / Previous-Year-
 *    visible condition it uses, NOT the consecutive-year gate). Its remaining
 *    mounted-only role is DISPLAY: it is the sole producer of the component's
 *    `equityBeginningEndErrors` red-border cell map (consumed by
 *    `hasEquityBeginningEndError()` / `...Dual()`), which the pure path has no
 *    equivalent of by design (the editor owns display). The earlier
 *    NON-consecutive-year divergence (mounted flagged Failed, headless did not)
 *    is therefore closed at the verdict level. NOTE: the converge-up is PENDING
 *    PO double-confirmation — if reversed, re-add the `areRole19YearsConsecutive`
 *    gate in `checkConsecutiveYearEquityRollForward` (the param is retained for
 *    exactly that).
 *  - Edit-mode / savedEditedRows → Edited, not Failed.
 *
 * ---- Cross-sheet dependency notes ----------------------------------------
 *
 *  - `filingPrerequisites` (Role 0) — required for column visibility and for
 *    driver #5's consecutive-year date gate (`checkCrossSheetEquityByYear`).
 *    The roll-forward (#3/#4) no longer consults it (converge-up). Tolerated
 *    absent: visibility defaults to "all four columns visible"; driver #5's
 *    consecutive check returns false and silently skips (matches the editor's
 *    `if (!deiData) return errors`), while the roll-forward still runs against
 *    whatever Previous column the default visibility exposes.
 *  - `statementOfProfitOrLoss` (Role 12) — `ProfitLoss` equality source.
 *    Tolerated absent: source coerces to null and the rule short-circuits.
 *  - `statementOfComprehensiveIncome` (Role 15) — `ComprehensiveIncome` /
 *    `OtherComprehensiveIncome` equality sources. Same absence tolerance.
 *  - `statementOfFinancialPosition` (Role 8) — `Equity` total for the
 *    `crossSheetEquityByYear` rule. Tolerated absent: rule silently skipped.
 *
 * See `role19-statement-of-changes-in-equity.rules.ts` for the shared primitives
 * (schema selection, key builders, visibility resolution).
 */

interface CrossSheetReader {
  read(sheet: string): Record<string, any> | undefined;
}

function makeCrossSheetReader(
  bundle: Record<string, Record<string, any>>
): CrossSheetReader {
  return {
    read(sheet: string) {
      return bundle[sheet];
    },
  };
}

/**
 * Materialise the cross-sheet bundle (a `Map`) into a plain record and recompute
 * every referenced sheet's calculated totals BEFORE any cross-sheet compare — so
 * the headless path compares against the SAME recomputed operands the editor sees
 * (filing-form-container recomputes every statement sheet at load). Role 19
 * compares against Role 8 `Equity` (driver #5), Role 12 `ProfitLoss` and Role 15
 * `ComprehensiveIncome` / `OtherComprehensiveIncome` (driver #2) — all calculated
 * totals. Reading any raw was the false-`Success*` parity gap. Visibility slices
 * (filingPrerequisites / filingInfo) have no calculator and pass through
 * untouched, so the same reader serves both visibility and numeric reads.
 * Mirrors the `recomputeReferencedSheets` step role 8 / role 15 already run.
 */
function buildRole19RecomputedBundle(
  schema: Role19Schema,
  context: SheetValidationContext,
  taxonomy: 'MFRS' | 'MPERS'
): Record<string, Record<string, any>> {
  const bundle: Record<string, Record<string, any>> = {};
  if (context.crossSheetData) {
    for (const [key, value] of context.crossSheetData) {
      bundle[key as string] = value as Record<string, any>;
    }
  }
  // Referenced operands (Role 12 ProfitLoss, Role 15 Comprehensive/Other CI,
  // Role 8 Equity) are recompute-of-latest — never the N8N snapshot (revised
  // global rule). The roster and the schema provider are the SHARED ones from
  // the rules module, so the mounted editor's cross-sheet messages are computed
  // over exactly these operands too.
  return recomputeReferencedSheets(
    bundle,
    role19ReferencedSheetKeys(schema),
    taxonomy,
    (sheetKey) => role19SchemaForReferencedSheet(sheetKey, taxonomy)
  ) as Record<string, Record<string, any>>;
}

/**
 * Parse a raw cell value into a number. Mirrors the
 * `(v !== null && v !== undefined && v !== '') ? Number(v) : null` pattern
 * used across validation-field-value.util; returns null for unparseable values.
 */
function coerceNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Column-aware cell fallback for cross-sheet equality. Mirrors
 * validation-field-value.util's getFieldValueForColumn — when the source cell is
 * missing on a `calculated` / `readonly` control, the editor returns 0
 * ("calculated fields display 0 when null"). For regular rows the fallback
 * is null (skip-when-null).
 */
function readColumnCell(
  payload: Record<string, any> | undefined,
  key: string,
  control: Role19SchemaControl | undefined
): number | null {
  if (!payload) return null;
  const raw = payload[key];
  if (!isRole19Empty(raw)) return coerceNumber(raw);
  return isRole19CalculatedField(control) ? 0 : null;
}

/**
 * Driver #1: required-field empties.
 *
 * Walks every monetary-row control with a schema-declared required flag
 * (using the LB-001 fix-guard — see `isRole19StaticallyRequired`). For every
 * visible (equityColumn × consolidation × period) cell, null → Failed.
 *
 * Returns on first miss because `evaluateSheetStatus()` only needs a
 * true/false answer and the editor itself short-circuits on first Failed row.
 */
function checkRequiredFieldErrors(
  schema: Role19Schema,
  sheetData: Record<string, any>,
  visibleColumns: ReadonlyArray<Role19ColumnVisibility>
): boolean {
  const equityColumns = schema.equityColumns ?? [];
  if (equityColumns.length === 0) return false;

  for (const control of schema.controls) {
    if (!isRole19StaticallyRequired(control)) continue;
    if (control.ui?.equityColumns !== true) continue;

    for (const column of visibleColumns) {
      if (!column.visible) continue;
      for (const equityColumn of equityColumns) {
        const key = buildRole19EquityColumnKey(
          control.qname,
          equityColumn.qname,
          column.consolidationType,
          column.periodSuffix
        );
        if (isRole19Empty(sheetData[key])) {
          return true;
        }
      }
    }
  }
  return false;
}

/**
 * Driver #2: schema-declared column-aware equality against roles 12 & 15.
 *
 * Mirrors the `equityColumns` branch of
 * `EqualityValidationService.validateAllEqualityRules`:
 *   - SOCIE side: `{qname}:{totalColumnQname}[Cons][Period]`
 *   - Cross-sheet side (roles 12 / 15 = dualColumn): `{qname}[Cons][Period]`
 * Rule's `taxonomy` filter is honoured; null/empty sides follow the skip
 * rules (skip when both empty; rule-level skipIfSourceNull / skipIfTargetNull).
 * Calculated/readonly source cells missing → treated as 0 (same posture as
 * `getFieldValueForColumn`).
 *
 * SOCIE schemas today declare NO `operation: 'sum'` equality — if a future
 * schema edit introduces one, extend this helper (flag in the ledger).
 */
function checkCrossSheetEqualityErrors(
  schema: Role19Schema,
  sheetData: Record<string, any>,
  reader: CrossSheetReader,
  visibleColumns: ReadonlyArray<Role19ColumnVisibility>,
  accountingStandard: string | undefined | null,
  taxonomy: 'MFRS' | 'MPERS'
): boolean {
  const totalColumnQname = getRole19TotalColumnQname(accountingStandard);

  for (const control of schema.controls) {
    const rules = control.validation?.equality;
    if (!rules || rules.length === 0) continue;

    // Skip sum-style equality — none declared on role 19 today; see header.
    if (rules.every((rule) => rule.operation === 'sum')) continue;

    for (const rule of rules) {
      if (
        rule.taxonomy &&
        rule.taxonomy.toUpperCase() !== taxonomy.toUpperCase()
      ) {
        continue;
      }

      // Source sheet: roles 12 / 15 are `dualColumn` and use the bare qname
      // with [Cons][Period] suffix (no `:{columnQname}` infix). When the
      // equality target is SOCIE itself, we'd reuse the equityColumns key
      // format — no such rule exists today but guard for future edits.
      const sourcePayload = reader.read(rule.sheet);

      for (const column of visibleColumns) {
        if (!column.visible) continue;

        const socieKey = buildRole19EquityColumnKey(
          control.qname,
          totalColumnQname,
          column.consolidationType,
          column.periodSuffix
        );
        const sourceKey = buildRole19SfpKey(
          rule.field,
          column.consolidationType,
          column.periodSuffix
        );

        // Current (SOCIE) side — follow the same calculated-or-readonly
        // fallback the editor applies on its own form control.
        const rawCurrent = sheetData[socieKey];
        const rawSource = sourcePayload?.[sourceKey];

        // Editor: skip when both null (nothing to compare).
        if (isRole19Empty(rawCurrent) && isRole19Empty(rawSource)) continue;

        if (rule.skipIfSourceNull && isRole19Empty(rawCurrent)) continue;
        if (rule.skipIfTargetNull && isRole19Empty(rawSource)) continue;

        // Editor: treat null as 0 for comparison. NaN neutralised too.
        const currentNum = coerceNumber(rawCurrent) ?? 0;
        const sourceNum = coerceNumber(rawSource) ?? 0;

        if (differsAtCentPrecision(currentNum, sourceNum)) {
          return true;
        }
      }
    }
  }
  return false;
}

/**
 * Drivers #3 + #4: SOCIE roll-forward — consecutiveYearEquity (non-total
 * equity columns) and consecutiveYearEquityTotal (Total column only).
 *
 * For each control declaring a `consecutiveYearEquity` or
 * `consecutiveYearEquityTotal` rule, compare:
 *
 *   Beginning-cell (CurrentYear table) === End-cell (PreviousYear table)
 *
 * column by column for every VISIBLE Consolidation axis. Fires on EVERY
 * Subsequent filing where the Previous-Year column is visible — it is NOT
 * gated on the financial years being consecutive.
 *
 * CONVERGE-UP (raised 2026-06-02, PENDING PO double-confirmation): the
 * `consecutiveYearEquity`-named rules are processed unconditionally because
 * equity is CONTINUOUS — the opening balance of the current period must equal
 * the closing balance of the prior period regardless of any gap/overlap
 * between the two financial years (a change of financial year-end does not
 * break that continuity). This deliberately mirrors the mounted editor's
 * `updateEquityBeginningEndCellErrors()`, which gates on `hasPreviousYear`
 * alone (NOT the FY-boundary date check), so the headless verdict no longer
 * under-reports a Beginning≠End break on a non-consecutive boundary. The
 * companion mounted method `validateConsecutiveYearEquity()` keeps its
 * date gate, but its result is a strict subset of the unconditional
 * `updateEquityBeginningEndCellErrors()` union it sits beside, so the mounted
 * EFFECTIVE verdict equals this unconditional check. (`areRole19YearsConsecutive`
 * is still used by driver #5's `checkCrossSheetEquityByYear`, which is a
 * different SOCIE↔SFP check and out of scope for this convergence.)
 *
 * Note the period scoping: the editor's implementation iterates over
 * `consolidationTypes` only (not periodTypes — the rule is "Current vs
 * Previous" which requires BOTH periods to be present). So the skip
 * condition is "previous year not visible at all". Mirror that here by
 * short-circuiting when no Previous column is visible for either axis.
 */
function checkConsecutiveYearEquityRollForward(
  schema: Role19Schema,
  sheetData: Record<string, any>,
  filingPrerequisites: Record<string, unknown> | undefined,
  visibleColumns: ReadonlyArray<Role19ColumnVisibility>,
  accountingStandard: string | undefined | null
): boolean {
  // NOTE: intentionally NOT gated on areRole19YearsConsecutive() — see the
  // CONVERGE-UP note above. `filingPrerequisites` is retained in the signature
  // because the caller passes it and a future re-gate (if the PO reverses this)
  // would need it; the visible-Previous-column short-circuit below is the only
  // gate now.
  void filingPrerequisites;

  const equityColumns = schema.equityColumns ?? [];
  if (equityColumns.length === 0) return false;

  const totalColumnQname = getRole19TotalColumnQname(accountingStandard);

  // Only the consolidation axes that have BOTH Current and Previous visible
  // can contribute (the rule compares Beginning[Current] vs End[Previous]).
  const axesToCheck: Role19ColumnVisibility[][] = [];
  for (const cons of ['Consolidated', 'Separate'] as const) {
    const current = visibleColumns.find(
      (column) => column.consolidationType === cons && column.periodType === 'Current'
    );
    const previous = visibleColumns.find(
      (column) => column.consolidationType === cons && column.periodType === 'Previous'
    );
    if (current?.visible && previous?.visible) {
      axesToCheck.push([current, previous]);
    }
  }
  if (axesToCheck.length === 0) return false;

  for (const control of schema.controls) {
    const perComponentRule = control.validation?.consecutiveYearEquity;
    const totalRule = control.validation?.consecutiveYearEquityTotal;
    if (!perComponentRule && !totalRule) continue;

    // End-control is always the rule's compareField — look it up once for
    // the calculated-cell fallback. Matches the editor's `endCtrl = find(...)`.
    const compareField =
      perComponentRule?.compareField ?? totalRule?.compareField ?? '';
    const endControl = schema.controls.find((control) => control.qname === compareField);

    for (const [current, previous] of axesToCheck) {
      // Per-component check: iterate equityColumns EXCLUDING the Total.
      if (perComponentRule) {
        if (
          !perComponentRule.taxonomy ||
          perComponentRule.taxonomy.toUpperCase() ===
            role19AccountingStandardToTaxonomy(accountingStandard).toUpperCase()
        ) {
          for (const equityColumn of equityColumns) {
            if (equityColumn.qname === totalColumnQname) continue;

            const beginningKey = buildRole19EquityColumnKey(
              control.qname,
              equityColumn.qname,
              current.consolidationType,
              current.periodSuffix
            );
            const endKey = buildRole19EquityColumnKey(
              compareField,
              equityColumn.qname,
              previous.consolidationType,
              previous.periodSuffix
            );

            const beginRaw = sheetData[beginningKey];
            const endRaw = sheetData[endKey];

            const beginNum =
              !isRole19Empty(beginRaw)
                ? coerceNumber(beginRaw)
                : isRole19CalculatedField(control)
                  ? 0
                  : null;
            const endNum =
              !isRole19Empty(endRaw)
                ? coerceNumber(endRaw)
                : isRole19CalculatedField(endControl)
                  ? 0
                  : null;

            // Editor: skip if either side is null / NaN (matches the
            // `if (beginNum === null || endNum === null || isNaN(...)) return`
            // guard in validateConsecutiveYearEquity).
            if (beginNum === null || endNum === null) continue;
            if (!Number.isFinite(beginNum) || !Number.isFinite(endNum)) continue;

            if (differsAtCentPrecision(beginNum, endNum)) {
              return true;
            }
          }
        }
      }

      // Total-column check.
      if (totalRule) {
        if (
          !totalRule.taxonomy ||
          totalRule.taxonomy.toUpperCase() ===
            role19AccountingStandardToTaxonomy(accountingStandard).toUpperCase()
        ) {
          const beginningKey = buildRole19EquityColumnKey(
            control.qname,
            totalColumnQname,
            current.consolidationType,
            current.periodSuffix
          );
          const endKey = buildRole19EquityColumnKey(
            compareField,
            totalColumnQname,
            previous.consolidationType,
            previous.periodSuffix
          );

          const beginRaw = sheetData[beginningKey];
          const endRaw = sheetData[endKey];

          const beginNum =
            !isRole19Empty(beginRaw)
              ? coerceNumber(beginRaw)
              : isRole19CalculatedField(control)
                ? 0
                : null;
          const endNum =
            !isRole19Empty(endRaw)
              ? coerceNumber(endRaw)
              : isRole19CalculatedField(endControl)
                ? 0
                : null;

          if (beginNum === null || endNum === null) continue;
          if (!Number.isFinite(beginNum) || !Number.isFinite(endNum)) continue;

          if (differsAtCentPrecision(beginNum, endNum)) {
            return true;
          }
        }
      }
    }
  }

  return false;
}

/**
 * Driver #5: cross-sheet equity reconciliation against Role 8 (SFP).
 *
 * Rule lives on SOCIE's `Equity:End` control. Compares SOCIE
 * `Equity:End:EquityMember[Cons][Period]` (the equity TOTAL column) vs SFP
 * `Equity[Cons][Period]` per visible column, at cent precision (any ≥ 0.01
 * difference → Failed); calculated-cell fallback treats missing cells as 0.
 * Operand read mirrors the editor: SOCIE `Equity:End` from the RECOMPUTED
 * `equalitySnapshot` (the caller passes it as `sheetData` — `Equity:End` is a
 * CALCULATED total the editor recomputes before validating; reading the raw
 * extraction here was the "X-until-visited then *" parity gap), SFP `Equity`
 * from the recomputed cross-sheet bundle.
 *
 * Period scope (AS-1076) — handled per-column in the loop:
 *   - CURRENT YEAR: compared UNGATED (current-year columns always render).
 *   - PREVIOUS YEAR: compared only on a consecutive FY boundary (Previous
 *     columns are hidden for first-time filers, reproducing the editor's
 *     isSubsequentPreparation() gate via visibility).
 */
function checkCrossSheetEquityByYear(
  schema: Role19Schema,
  sheetData: Record<string, any>,
  reader: CrossSheetReader,
  filingPrerequisites: Record<string, unknown> | undefined,
  visibleColumns: ReadonlyArray<Role19ColumnVisibility>,
  context: SheetValidationContext
): boolean {
  // Per-column gate (below), not a blanket early-return: a non-consecutive /
  // missing boundary must still let the Current-Year leg run.
  const consecutive = filingPrerequisites
    ? areRole19YearsConsecutive(filingPrerequisites)
    : false;

  const sfpData = reader.read(SHEET_KEYS.statementOfFinancialPosition);
  if (!sfpData) return false;

  const targets = getRole19EquityCrossSheetTargets(context.accountingStandard);
  const totalColumnQname = getRole19TotalColumnQname(
    context.accountingStandard
  );

  // Rule lives on SOCIE's Equity:End control (per the MFRS/MPERS schemas).
  // Editor's validateCrossSheetEquityByYear probes BOTH sides for the rule;
  // today only SOCIE declares it, so mirror that primary path.
  const socieControl = schema.controls.find(
    (control) => control.qname === targets.socieEquityEndQname
  );
  const rule = socieControl?.validation?.crossSheetEquityByYear;
  if (!rule) return false;

  const taxonomy = role19AccountingStandardToTaxonomy(
    context.accountingStandard
  );
  if (rule.taxonomy && rule.taxonomy.toUpperCase() !== taxonomy.toUpperCase()) {
    return false;
  }

  // Role 8's SFP `Equity` control is always calculated; we don't need its
  // schema to know that. The SOCIE side is also calculated per the schema.
  // Use the schema-driven probe so future edits that mark it editable get
  // picked up automatically.
  const sfpSchema = pickRole19Role8Schema(context.accountingStandard);
  const sfpControl = sfpSchema.controls.find(
    (control) => control.qname === targets.sfpEquityQname
  );

  for (const column of visibleColumns) {
    if (!column.visible) continue;

    // AS-1076 period scope: Current Year is ungated; Previous Year only on a
    // consecutive FY boundary.
    if (column.periodType === 'Previous' && !consecutive) continue;

    const socieKey = buildRole19EquityColumnKey(
      targets.socieEquityEndQname,
      totalColumnQname,
      column.consolidationType,
      column.periodSuffix
    );
    const sfpKey = buildRole19SfpKey(
      targets.sfpEquityQname,
      column.consolidationType,
      column.periodSuffix
    );

    const socieNum = readColumnCell(sheetData, socieKey, socieControl);
    const sfpNum = readColumnCell(sfpData, sfpKey, sfpControl);

    if (socieNum === null || sfpNum === null) continue;
    if (!Number.isFinite(socieNum) || !Number.isFinite(sfpNum)) continue;

    if (differsAtCentPrecision(socieNum, sfpNum)) {
      return true;
    }
  }

  return false;
}

/**
 * Driver #6: schema-driven column-sum reconciliation.
 *
 * For each schema `columnSumValidations` entry, for every row × visible
 * (consolidation × period), sub-total cell must equal the sum of component
 * cells. Mirrors `ColumnSumValidationService.validateColumnSum` exactly
 * (`parseFloat(...) || 0` coercion, 0.01 tolerance).
 *
 * IMPORTANT: the caller passes the RECOMPUTED `equalitySnapshot` (not the raw
 * persisted slice) as `sheetData` here — see the call site. The mounted editor
 * runs this over `form.getRawValue()` AFTER `calculateTotals()` recomputes each
 * sub-total column, so the snapshot is what keeps the two paths identical.
 */
function checkColumnSumErrors(
  schema: Role19Schema,
  sheetData: Record<string, any>,
  visibleColumns: ReadonlyArray<Role19ColumnVisibility>
): boolean {
  const configs = schema.columnSumValidations;
  if (!configs || configs.length === 0) return false;

  // Row candidates: non-header rows flagged `equityColumns`, matching
  // validateColumnSum's `rowQnames` computation.
  const rowControls = schema.controls.filter(
    (control) => !isRole19SectionHeader(control) && control.ui?.equityColumns === true
  );

  for (const config of configs) {
    // Honour the config's optional row allow-list. Mirrors
    // ColumnSumValidationService.validateColumnSum: present & non-empty → only
    // those rows; absent → every candidate row.
    const scopedRowControls =
      config.appliesToRowQnames && config.appliesToRowQnames.length > 0
        ? rowControls.filter((control) => config.appliesToRowQnames!.includes(control.qname))
        : rowControls;

    for (const control of scopedRowControls) {
      for (const column of visibleColumns) {
        if (!column.visible) continue;

        let expectedSum = 0;
        for (const componentQname of config.componentColumnQnames) {
          const componentKey = buildRole19EquityColumnKey(
            control.qname,
            componentQname,
            column.consolidationType,
            column.periodSuffix
          );
          const raw = sheetData[componentKey];
          // Editor uses `parseFloat(rawValue) || 0` — so null / undefined / ''
          // / NaN all coerce to 0.
          const parsed = parseFloat(raw as string);
          expectedSum += Number.isFinite(parsed) ? parsed : 0;
        }

        const subTotalKey = buildRole19EquityColumnKey(
          control.qname,
          config.subTotalColumnQname,
          column.consolidationType,
          column.periodSuffix
        );
        const rawSubTotal = sheetData[subTotalKey];
        const actualParsed = parseFloat(rawSubTotal as string);
        const actualValue = Number.isFinite(actualParsed) ? actualParsed : 0;

        if (differsAtCentPrecision(actualValue, expectedSum)) {
          return true;
        }
      }
    }
  }

  return false;
}

/**
 * Driver #7: MFRS Consolidated-only equity-total horizontal sum.
 *
 * For each row (excluding `IncreaseDecreaseThroughAcquisitionOfSubsidiary`),
 * for every visible Consolidated × (Current/Previous), the Total column
 * (`ifrs-full:EquityMember`) must equal the sum of:
 *   - EquityAttributableToOwnersOfParentMember
 *   - OtherComponentsOfEquityMember
 *   - NoncontrollingInterestsMember
 *
 * Tolerance 0.01. MPERS schema does not trigger this driver. Separate
 * tables are excluded even when they're visible (matches the
 * `consolidationTypes = ['Consolidated']` literal in the component).
 */
function checkEquityTotalHorizontalSumErrors(
  schema: Role19Schema,
  sheetData: Record<string, any>,
  visibleColumns: ReadonlyArray<Role19ColumnVisibility>
): boolean {
  if (schema.taxonomy !== 'FS-MFRS') return false;
  if (!schema.equityColumns || schema.equityColumns.length === 0) return false;

  // Restrict to Consolidated columns only, matching the component's guard.
  const consolidatedColumns = visibleColumns.filter(
    (column) => column.consolidationType === 'Consolidated' && column.visible
  );
  if (consolidatedColumns.length === 0) return false;

  const rowControls = schema.controls.filter(
    (control) =>
      !isRole19SectionHeader(control) &&
      control.ui?.equityColumns === true &&
      control.qname !== ROLE19_EQUITY_TOTAL_EXCLUDED_ROW
  );

  const totalQname = getRole19TotalColumnQname('MFRS') as string;

  for (const control of rowControls) {
    for (const column of consolidatedColumns) {
      const totalKey = buildRole19EquityColumnKey(
        control.qname,
        totalQname,
        column.consolidationType,
        column.periodSuffix
      );
      const totalParsed = parseFloat(sheetData[totalKey] as string);
      const totalValue = Number.isFinite(totalParsed) ? totalParsed : 0;

      let componentSum = 0;
      for (const componentQname of ROLE19_EQUITY_TOTAL_COMPONENT_COLUMNS) {
        const componentKey = buildRole19EquityColumnKey(
          control.qname,
          componentQname,
          column.consolidationType,
          column.periodSuffix
        );
        const parsed = parseFloat(sheetData[componentKey] as string);
        componentSum += Number.isFinite(parsed) ? parsed : 0;
      }

      if (differsAtCentPrecision(totalValue, componentSum)) {
        return true;
      }
    }
  }

  return false;
}

export const role19StatementOfChangesInEquityValidator: SheetValidator = {
  sheetKey: SHEET_KEYS.statementOfChangesInEquity,
  // Tier 4: cross-sheet reads (filingPrerequisites for visibility + role 8
  // for crossSheetEquityByYear + role 12 for ProfitLoss equality + role 15
  // for ComprehensiveIncome / OtherComprehensiveIncome equality) PLUS
  // schema-driven column sums, equity-total horizontal sums, and the SOCIE
  // roll-forward (consecutiveYearEquity + consecutiveYearEquityTotal). The
  // dual-path parity harness opts this validator into runtime comparison
  // against the mount-based path on every updateSheetLevelStatus emission.
  tier: 4,
  validate(
    sheetData: Record<string, any>,
    context: SheetValidationContext
  ): PureSheetValidationResult {
    const schema = pickRole19Schema(context.accountingStandard);
    // Taxonomy is resolved up-front (no reader dependency) so it can drive the
    // recomputed-bundle build below.
    const contextTaxonomy = role19AccountingStandardToTaxonomy(
      context.accountingStandard
    );
    // Read cross-sheet slices through the RECOMPUTED bundle so the cross-sheet
    // operands (Role 8 Equity, Role 12 ProfitLoss, Role 15 ComprehensiveIncome)
    // match the editor's recomputed values, not the raw N8N extraction.
    const reader = makeCrossSheetReader(
      buildRole19RecomputedBundle(schema, context, contextTaxonomy)
    );
    const filingPrerequisites = reader.read(SHEET_KEYS.filingPrerequisites);
    // Column visibility comes from the SHARED, config-driven resolver
    // (dimension-visibility.util + registry), routed through the role-19
    // adapter `resolveRole19ColumnVisibility` which re-shapes the shared
    // `{ dimension, period, visible }` cells into the richer role-19 cell shape
    // (`consolidationType` / `periodSuffix` / `periodType` / `label`) the
    // key-builders and roll-forward loops below consume. The same resolver +
    // registry feeds the mounted editor via CrossReferenceService.getVisibility(),
    // so the on-screen columns and the validated columns cannot drift. Slices
    // are passed prereq-first then filingInfo (the first-listing-visit fallback,
    // mirror of the role 12 lift): when the editor has not mounted yet
    // `filingPrerequisites` is undefined, but Nature / preparation-type are also
    // persisted on the backend's `filingInfo` slice.
    const visibleColumns = resolveRole19ColumnVisibility({
      filingPrerequisites,
      filingInfo: reader.read(SHEET_KEYS.filingInfo),
    });

    // Taxonomy strict-mode guard — validateAllEqualityRules short-circuits
    // when the schema's taxonomy and the filing's accounting standard
    // disagree. By construction pickRole19Schema returns a matching schema,
    // but keep the guard for unrecognised `accountingStandard` values.
    // (contextTaxonomy is resolved at the top of validate() so it can feed the
    // recomputed-bundle build.)
    const schemaTaxonomy = extractRole19SchemaTaxonomy(schema);
    const taxonomiesAgree =
      schemaTaxonomy !== null && schemaTaxonomy === contextTaxonomy;

    // Driver #1: required-field empties. Fires unconditionally (not gated on
    // taxonomy) because validateRequiredFields() doesn't consult taxonomy.
    // Reads the persisted payload — mandatory presence is checked on the
    // entered values, not recomputed totals.
    if (checkRequiredFieldErrors(schema, sheetData, visibleColumns)) {
      return { hasErrors: true, hasSuggestedChanges: false };
    }

    // Recompute the calculated equity totals (e.g. ComprehensiveIncome =
    // ProfitLoss + OtherComprehensiveIncome) up-front and overlay them on the
    // persisted payload, so the cross-sheet equality check below compares the
    // SAME operand the mounted editor does (it calls calculateTotals() before
    // validating). This recompute previously ran only AFTER the equality
    // checks — purely to drive the Validated* asterisk — so the equality
    // compared the raw N8N-extracted ComprehensiveIncome rather than the
    // recomputed total, letting a sheet whose extracted CI happened to tie
    // with role 15 pass on the listing while opening the sheet (which
    // recomputes) failed it. The original `sheetData` is preserved for the
    // asterisk diff below.
    //
    // The snapshot is fed to the cross-sheet equality check (#2) AND the
    // equity-total horizontal-sum driver (#7), because the mounted editor runs
    // validateEquityTotal() AFTER calculateTotals() patches the recomputed
    // vertical totals into the form — so it validates the SAME recomputed
    // operands (EquityMember / owners / otherComponents / NCI) this snapshot
    // carries. Reading the raw persisted payload for #7 instead made the
    // headless path raise a BLOCKING error on an N8N extraction whose stored
    // Equity:End breakdown did not tie out horizontally (extracted owners
    // RM3,057.09 + siblings ≠ extracted EquityMember total), while the editor
    // recomputed owners (restated + ΔEquity) so the row reconciled and surfaced
    // only the non-blocking "Validated*" asterisk — a green-tick-on-open /
    // red-cross-on-listing parity gap (the dev parity check also warned the pure
    // path flagged an error the editor never did). Overlaying does NOT neutralise
    // #7: `calculateTotals_610000` recomputes only the four VERTICAL totals per
    // column, so every INPUT/movement row keeps its raw entered values and a real
    // imbalance that survives the recompute still propagates into the recomputed
    // totals and fires.
    //
    // Driver #6 (column-sum) is fed the SAME recomputed `equalitySnapshot` as #2
    // and #7 — it must NOT read the raw persisted payload. An earlier note here
    // claimed the column sub-totals (`EquityAttributableToOwnersOfParentMember`,
    // `OtherReservesMember`, `NonDistributableReservesMember`) were "NOT among the
    // recomputed totals"; that was factually wrong. `calculateTotals_610000`
    // recomputes each sub-total COLUMN as the horizontal sum of its component
    // columns for EVERY row (movement rows included), so the mounted editor —
    // which runs `validateColumnSum()` over `form.getRawValue()` AFTER
    // `calculateTotals()` patches those sub-totals in — always sees the recomputed
    // (self-consistent) sub-total, and its column-sum check passes. Reading the raw
    // persisted payload here instead made the headless path raise a BLOCKING error
    // on a v2-direct-LLM extraction whose stored sub-total did not tie out
    // horizontally (e.g. ComprehensiveIncome owners-of-parent RM107.80 vs its
    // component columns summing to RM62.72), while the editor recomputed the
    // sub-total to RM62.72 so the row reconciled and surfaced only the non-blocking
    // "Validated*" asterisk — the reported "Review on the listing / X-until-visited
    // then * once opened" parity gap. `equalitySnapshot` = `{ ...persisted,
    // ...recomputed }` keeps every component/input cell raw and overlays only the
    // recomputed sub-totals, so it equals the editor's post-`calculateTotals()`
    // form value cell-for-cell — making #6 headless-vs-mounted-identical. The
    // extraction-vs-recompute divergence still surfaces, correctly, as the
    // non-blocking `hasSuggestedChanges` (`*`) via `hasCalculatedFieldMismatch`
    // below. Row scoping is unaffected: the overlay is per (row × column) cell, so
    // an out-of-scope row's leaf never bleeds into an in-scope row.
    //
    // The roll-forward (#3/#4) and equity-by-year (#5) drivers ALSO read from the
    // recomputed `equalitySnapshot`, not the raw persisted payload. Their compared
    // operand `Equity:End` (the SOCIE closing-equity row) is a CALCULATED total —
    // `calculateTotals_610000` recomputes it (owners + otherComponents + NCI, and
    // Restated + ChangesInEquity) — so the mounted editor, which runs
    // `calculateTotals()` before validating, always compares the RECOMPUTED
    // `Equity:End`. An earlier note here claimed these operands were "out of scope
    // for the recomputed-total gap"; that was WRONG for `Equity:End`. Reading the
    // raw payload made the headless path compare the raw-extracted `Equity:End`
    // (e.g. an N8N extraction whose stored Total column tied to a memberless
    // `Equity:End` value of RM527.60 that collides with — and loses to — the
    // member-qualified RM1,121.60 on transform) against the RECOMPUTED SFP `Equity`
    // (RM1,121.60), raising a BLOCKING cross-sheet error (X) the editor never shows;
    // opening the sheet recomputed `Equity:End` to RM1,121.60 so it tied and
    // surfaced only the non-blocking `Validated*` asterisk — the "Review on the
    // listing / X-until-visited then * once opened" parity gap. `equalitySnapshot`
    // keeps `Equity:Beginning` (an INPUT row, not recomputed) raw and overlays only
    // the recomputed `Equity:End`, so it equals the editor's post-`calculateTotals()`
    // form value cell-for-cell — making #3/#4/#5 headless-vs-mounted-identical.
    // The extraction-vs-recompute divergence still surfaces, correctly, as the
    // non-blocking `hasSuggestedChanges` (`*`) via `hasCalculatedFieldMismatch`.
    const recomputed = calculator.calculateTotals_610000(
      sheetData,
      accountingStandardToXbrlPrefix(context.accountingStandard),
      {
        hasEquityColumns:
          Array.isArray(schema.equityColumns) && schema.equityColumns.length > 0,
        equityColumns: schema.equityColumns,
        visibleColumns,
      }
    );
    const equalitySnapshot = buildEqualitySnapshot(sheetData, recomputed);

    // Drivers #2 + #3 + #4 + #5 + #6 + #7 all depend on the taxonomy guard
    // (either because the editor's validateAllEqualityRules gates on it OR
    // because the rule only exists on the MFRS schema, e.g. driver #7).
    if (taxonomiesAgree) {
      if (
        checkCrossSheetEqualityErrors(
          schema,
          equalitySnapshot,
          reader,
          visibleColumns,
          context.accountingStandard,
          contextTaxonomy
        )
      ) {
        return { hasErrors: true, hasSuggestedChanges: false };
      }

      if (
        checkConsecutiveYearEquityRollForward(
          schema,
          equalitySnapshot,
          filingPrerequisites,
          visibleColumns,
          context.accountingStandard
        )
      ) {
        return { hasErrors: true, hasSuggestedChanges: false };
      }

      // AS-1076: single reconciliation covering both Current Year (ungated) and
      // Previous Year (consecutive-gated) — see checkCrossSheetEquityByYear.
      if (
        checkCrossSheetEquityByYear(
          schema,
          equalitySnapshot,
          reader,
          filingPrerequisites,
          visibleColumns,
          context
        )
      ) {
        return { hasErrors: true, hasSuggestedChanges: false };
      }

      if (checkColumnSumErrors(schema, equalitySnapshot, visibleColumns)) {
        return { hasErrors: true, hasSuggestedChanges: false };
      }

      if (
        checkEquityTotalHorizontalSumErrors(
          schema,
          equalitySnapshot,
          visibleColumns
        )
      ) {
        return { hasErrors: true, hasSuggestedChanges: false };
      }
    }

    // AS-842 calc-diff: surface the editor's `Validated*` signal in the
    // silent path by diffing the equity totals recomputed above (via the pure
    // `CalculationService.calculateTotals_610000`) against the persisted
    // (N8N-extracted) values. Any mismatch flips `hasSuggestedChanges`, which
    // the resolver rolls up into a `Success*` conversion-level chip.
    //
    // The recompute (now performed before the equality checks) respects the
    // table-count permutation (1/2/4 visible tables based on
    // `showConsolidatedColumns × hasPreviousYear` from filing prerequisites —
    // see AS-842 §12.4) via the same visibility array used by the error
    // checks. Uses the ORIGINAL persisted `sheetData` (not the equality
    // snapshot): the asterisk reports "sum vs extracted" divergence, which
    // only exists relative to the raw extraction.
    return {
      hasErrors: false,
      hasSuggestedChanges:
        isResolvedAccountingStandard(context.accountingStandard) &&
        hasCalculatedFieldMismatch(context.extractedBaseline ?? sheetData, recomputed),
    };
  },
};
